<template>
  <v-container fluid class="pa-0">

    <!-- Header -->
    <div class="d-flex justify-space-between align-center mb-7">
      <div>
        <div style="font-size: 30px; font-weight: 800">My OJT Submissions</div>
        <div class="text-subtitle-2 mt-1">
          <span class="text-grey">Upload and track your daily and weekly OJT submissions</span>
        </div>
      </div>
      <v-btn
        color="#00A63E"
        class="white--text"
        @click="addnew"
        prepend-icon="mdi-plus"
        rounded="lg"
        size="large"
      >
        Add New
      </v-btn>
    </div>

    <!-- Search bar -->
    <v-card flat class="pa-4 rounded-xl mb-5 search-card">
      <v-text-field
        v-model="search"
        placeholder="Search submissions..."
        prepend-inner-icon="mdi-magnify"
        variant="outlined"
        density="compact"
        hide-details
        clearable
      />
    </v-card>

    <!-- Data Table -->
    <v-card flat class="rounded-xl table-card">
      <v-data-table
        :headers="headers"
        :items="data"
        :search="search"
        :loading="loading"
        hover
        class="rounded-xl"
      >
        <template v-slot:[`item.submitted_at`]="{ item }">
          <span v-if="item.submitted_at">{{ formatDate(item.submitted_at) }}</span>
          <span v-else class="text-grey text-caption font-italic">—</span>
        </template>

        <template v-slot:[`item.submitted_status`]="{ item }">
          <v-chip v-if="item.submitted_status == 1"      size="x-small" color="warning" variant="tonal">For Approval</v-chip>
          <v-chip v-else-if="item.submitted_status == 2" size="x-small" color="success" variant="tonal">Approved</v-chip>
          <v-chip v-else-if="item.submitted_status == 3" size="x-small" color="error"   variant="tonal">Returned</v-chip>
          <v-chip v-else                                  size="x-small" variant="outlined" color="grey">No Submission</v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex align-center gap-1 py-1">
            <v-btn
              v-if="item.submitted_status != 3"
              size="small"
              color="primary"
              variant="tonal"
              prepend-icon="mdi-eye-outline"
              rounded="lg"
              @click="viewItem(item)"
            >View</v-btn>

            <template v-if="item.submitted_status == 3">
              <v-btn
                size="small"
                color="primary"
                variant="tonal"
                prepend-icon="mdi-pencil-outline"
                rounded="lg"
                @click="editItem(item)"
              >Update</v-btn>
              <v-btn
                size="small"
                color="error"
                variant="tonal"
                icon="mdi-delete-outline"
                rounded="lg"
                @click="deleteItem(item)"
              />
            </template>
          </div>
        </template>

        <template #no-data>
          <div class="text-center py-10">
            <v-icon size="44" color="grey-lighten-1">mdi-folder-open-outline</v-icon>
            <div class="text-body-2 text-grey mt-2">No submissions yet.</div>
            <div class="text-caption text-grey-lighten-1">Click "Add New" to submit your first OJT document.</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Dialogs -->
    <StudentSubmissionDialog :data="itemData" :action="action" />

    <v-dialog v-model="dialogConfirmDelete" max-width="440">
      <v-card rounded="xl">
        <v-card-title class="text-body-1 font-weight-bold pa-5 pb-2">Confirm Deletion</v-card-title>
        <v-card-text class="text-body-2 text-grey-darken-1 pb-4">
          Are you sure you want to delete this submission? This action cannot be undone.
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn variant="text" @click="dialogConfirmDelete = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" rounded="lg" @click="confirmDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <fade-away-message-component
      displayType="default"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    />
  </v-container>
</template>

<script>
import eventBus from "@/eventBus";
import StudentSubmissionDialog from "../Dialogs/Forms/StudentSubmissionDialog.vue";
export default {
  components: {
    StudentSubmissionDialog,
  },
  data: () => ({
    search: "",
    headers: [
      { title: "Name", value: "sr_name", align: "start" },
      { title: "Status", value: "submitted_status", align: "center" },
      { title: "Submission Date", value: "submitted_at", align: "center" },
      {
        title: "Actions",
        value: "actions",
        align: "center",
        sortable: false,
        width: 220,
      },
    ],
    data: [],
    totalCount: 0,
    deleteData: null,
    itemData: null,
    uploadData: null,
    alerted: false,
    loading: false,
    options: {},
    action: null,
    paginationData: {},
    formdata: [],
    work_dates_menu: false,
    dialogConfirmDelete: false,
    fadeAwayMessage: {
      show: false,
      type: "success",
      header: "System Message!",
      message: "",
      top: 10,
    },
  }),
  mounted() {
    this.initialize();
    eventBus.on("closeStudentSubmissionDialog", () => {
      this.initialize();
    });
  },
  beforeUnmount() {
    eventBus.off("closeStudentSubmissionDialog");
  },
  methods: {
    pagination(data) {
      this.paginationData = data;
    },
    initialize() {
      this.loading = true;
      this.axiosCall(
        "/student-requirement-submission/getMy/getSubmissions",
        "GET",
      ).then((res) => {
        if (res) {
          this.data = res.data;
          this.loading = false;
        }
      });
    },
    addnew() {
      this.itemData = [{ id: null }];
      this.action = "Add";
    },
    deleteItem(item) {
      this.dialogConfirmDelete = true;
      this.deleteData = item;
    },
    editItem(item) {
      this.itemData = item;
      this.action = "Update";
    },
    viewItem(item) {
      this.itemData = item;
      this.action = "View";
    },
    confirmDelete() {
      this.axiosCall("/request-type/" + this.deleteData.id, "DELETE").then(
        () => {
          this.fadeAwayMessage.show = true;
          this.itemData = null;
          this.dialogConfirmDelete = false;
          this.initialize();
        },
      );
    },
  },
};
</script>

<style scoped>
.search-card {
  border: 1px solid #d1d5db !important;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06) !important;
}
.table-card {
  border: 1px solid #d1d5db !important;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06) !important;
}
.gap-1 { gap: 4px; }
</style>
